import { useCallback, useEffect, useState } from 'react';
import API_BASE_URL from 'src/constants';
import { components, paths } from 'src/generated/v1';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import logger from 'src/utils/logger';
import { UseFiltersInterface } from './filters';
import { $api, $apiFetchClient } from './api';
import { invalidateProductQueries } from './productsNew';
import { buildAndOrDomain, buildDomainString } from '../utils/general';
import { handleUnauthorized } from './authToken';

type StockData = paths['/stock/inventory']['get']['responses']['200']['content']['application/json'];

export type StockItem = StockData['items'][number];
export type InventoryProduct = components['schemas']['InventoryProduct'];

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  invalidateProductQueries(queryClient);
}

export const useStockInventoryQuery = (params: paths['/stock/inventory']['get']['parameters']['query']) => {
  return $api.useQuery('get', '/stock/inventory', {
    params: {
      query: params,
    },
  });
};

export const useAllInventoryProducts = () => {
  return useQuery({
    queryKey: ['get', '/stock/inventory', 'all'],
    queryFn: async () => {
      const size = 100;
      const fetchPage = async (page: number) => {
        const { data, error } = await $apiFetchClient.GET('/stock/inventory', {
          params: { query: { page, size } },
        });
        if (error) throw new Error('Failed to load inventory products');
        return data;
      };

      const first = await fetchPage(1);
      const pages = first?.pages ?? 1;
      const rest = await Promise.all(Array.from({ length: Math.max(pages - 1, 0) }, (_, i) => fetchPage(i + 2)));

      return [first, ...rest].flatMap(page => page?.items ?? []) as StockItem[];
    },
  });
};

export const fetchAllProductsForExport = async () => {
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken) {
    throw new Error('Unauthorized');
  }

  const response = await fetch(`${API_BASE_URL}portal/ai_report/products`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch products for export');
  }

  return response.json();
};

export const fetchAllProductIds = async (domain?: string): Promise<number[]> => {
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken) {
    throw new Error('Unauthorized');
  }

  let url = `${API_BASE_URL}portal/stock/inventory/ids`;
  if (domain) {
    url += `?domain=${encodeURIComponent(domain)}`;
  }

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch product ids');
  }

  return response.json();
};

export const fetchProductLots = async (productId: number) => {
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken) {
    throw new Error('Unauthorized');
  }

  const response = await fetch(`${API_BASE_URL}portal/products/${productId}/lots/`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch lots for product ${productId}`);
  }

  return response.json();
};

export const useCancelProducts = () => {
  const queryClient = useQueryClient();
  return $api.useMutation('put', '/utility/bulk_action/{model}/cancel', {
    onSuccess() {
      invalidate(queryClient);
    },
  });
};

// Custom hook to manage sale orders
//
// `options.baseAndDomain` lets a caller impose a hard constraint that must hold
// regardless of the user's search (e.g. only confirmed products in the
// outbound/inbound product pickers). It is AND-ed with the search filters —
// unlike the plain `domain` arg, which buildDomainString only OR-joins — and is
// applied to every fetch, including the initial load triggered inside this hook.
const useStockInventory = (
  filterInstance?: UseFiltersInterface,
  options?: { baseAndDomain?: { key: string; value: string; operator: string }[] },
) => {
  const baseAndDomain = options?.baseAndDomain;
  const accessToken = localStorage.getItem('access_token');
  const [stockInventory, setStockInventory] = useState<StockData>({ items: [], total: 0, page: 0, size: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(
    async (url: string, options: RequestInit = {}) => {
      setLoading(true);
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleUnauthorized(accessToken);
          }
          throw new Error('Failed to fetch data');
        }

        const d = await response.json();
        return d;
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  const getStockInventory = async (
    page: number = 1,
    size: number = 20,
    domain?: { key: string; value: string; operator: string }[],
  ) => {
    try {
      const filter = filterInstance?.filter;
      let url = `${API_BASE_URL}portal/stock/inventory?page=${page}&size=${size}`;
      if (filter?.id) {
        url = `${API_BASE_URL}portal/stock/inventory?filter_id=${filter.id}&page=${page}&size=${size}`;
      }

      const domainStr = baseAndDomain?.length ? buildAndOrDomain(baseAndDomain, domain) : buildDomainString(domain);
      if (domainStr) {
        url += `&domain=${encodeURIComponent(domainStr)}`;
      }

      const data = await fetchData(url);
      setStockInventory(data);
    } catch (err) {
      logger.error('Failed to fetch stock inventory', err);
      setError('Failed to fetch sale orders');
    }
  };

  useEffect(() => {
    getStockInventory();
  }, [filterInstance?.filter]);

  return { stockInventory, loading, error, getStockInventory, filterInstance };
};

export default useStockInventory;
